<template>
    <div>
        <div class="columns">
            <div class="column">
                <activity-generating-placeholder
                    v-if="isGenerationStatusUiEnabled && (isGenerating || isFailedGeneration)"
                    activity-type="video"
                    :generation-status="generationStatus"
                    :generation-error="generationError"
                />

                <template v-else-if="video">
                    <video-player :video-id="video.id" />

                    <div class="m-t-md">
                        <h4 class="title is-4">{{ video.title }}</h4>

                        <p v-if="video.caption" class="has-text-subtle">
                            {{ video.caption }}
                        </p>
                    </div>
                </template>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <e-field>
                    <display-content :content="activityContent.content" />
                </e-field>
            </div>
        </div>

        <div v-if="activity.activity_settings.acknowledgment && !readOnly" class="m-b-md">
            <e-field label="Acknowledgment">
                <e-checkbox v-if="!completed" v-model="userAccepted">
                    {{ activity.activity_content.acknowledgment_message }}
                </e-checkbox>

                <!-- eslint-disable-next-line vue/prefer-true-attribute-shorthand -->
                <e-checkbox v-else disabled model-value>
                    {{ activity.activity_content.acknowledgment_message }}
                </e-checkbox>
            </e-field>
        </div>

        <activity-completion-action-row>
            <e-button
                v-if="userHasAuthorizedAttempt || (!completed && !readOnly)"
                :loading="isLoading"
                :disabled="disable"
                type="is-primary"
                @click="submit"
                @keyup="submit"
            >
                {{ translate('mark_complete') }}
            </e-button>
        </activity-completion-action-row>

        <div v-if="!readOnly && canViewSubmissions" class="columns">
            <div class="column">
                <submissions :namespace="namespace" @fetch-submissions="toggleSubmissionLoading" />
            </div>
        </div>
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import ActivityGeneratingPlaceholder from '@/components/activity-types/course-activity-types/activities/shared/ActivityGeneratingPlaceholder.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import VideoPlayer from '@/components/videos/VideoPlayer.vue';
import { useActivity } from '@/composables/useActivity.js';
import { ACTIVITY_GENERATION_STATUS_UI_ENABLED } from '@/constants/activityGenerationStatusFlag.js';
import axios from '@/plugins/axios.js';

const PENDING_GENERATION_STATUSES = ['pending', 'queued', 'processing', 'generating'];
const POLL_INTERVAL_MS = 15000;

export default {
    name: 'VideoTemplate',

    components: {
        ActivityCompletionActionRow,
        ActivityGeneratingPlaceholder,
        DisplayContent,
        Submissions,
        VideoPlayer,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        const api = useActivity(props, ctx, { getPayload: () => instance.proxy.payload });

        return { translate, ...api };
    },

    data: () => ({
        video: null,
        userAccepted: false,
        generationStatus: null,
        generationError: null,
        statusPollIntervalId: null,
    }),

    computed: {
        isGenerationStatusUiEnabled: () => ACTIVITY_GENERATION_STATUS_UI_ENABLED,

        isGenerating() {
            if (PENDING_GENERATION_STATUSES.includes(this.generationStatus)) return true;
            return (
                !!this.generationStatus &&
                this.generationStatus !== 'failed' &&
                !this.video &&
                !this.activityContent?.video_id
            );
        },

        isFailedGeneration() {
            return this.generationStatus === 'failed';
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: { acknowledgment: this.userAccepted },
            };
        },

        disable() {
            if (this.activity.activity_settings.acknowledgment) {
                return this.disabled || !this.userAccepted;
            }

            return this.disabled;
        },
    },

    watch: {
        completed(completed) {
            if (completed) {
                this.userAccepted = true;
            }
        },
    },

    async created() {
        await this.loadVideo();
    },

    beforeUnmount() {
        this.clearStatusPolling();
    },

    methods: {
        async loadVideo() {
            const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);

            const endpoint = this.namespace === 'Activities' ? 'activities' : 'activity-templates';

            const { data } = await axios.get(`/api/${endpoint}/${id}?include=contentable.video`);

            this.generationStatus = data.data?.activity_content?.generation_status ?? null;
            this.generationError = data.data?.activity_content?.generation_error ?? null;

            // The video reference is linked at generation submit-time, but the playable
            // transcode is only ready once MediaConvert completes. Fetching the video here
            // (when a reference_id exists) also triggers the show-endpoint MediaConvert poll,
            // which self-heals a missed completion webhook. Guard against a null video.
            const referenceId = data.data?.activity_content?.video?.reference_id;

            if (referenceId) {
                this.video = await this.$store.dispatch('Videos/fetchOne', referenceId);
            }

            this.ensureStatusPolling();
        },

        ensureStatusPolling() {
            if (!this.isGenerating) {
                this.clearStatusPolling();
                return;
            }
            if (this.statusPollIntervalId) return;

            this.statusPollIntervalId = setInterval(() => {
                this.loadVideo().catch(() => {});
            }, POLL_INTERVAL_MS);
        },

        clearStatusPolling() {
            if (this.statusPollIntervalId) {
                clearInterval(this.statusPollIntervalId);
                this.statusPollIntervalId = null;
            }
        },
    },
};
</script>
